import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BATH_ROOM_SETTING, BUTTON } from '@core/constants/text.common';
import { BathDaiApplyResult } from '@core/models/models/bathe.model';
import { ApplyResultBlock, ApplyResultBlockKey, ApplyResultView, buildApplyResultView } from '@core/helper/bath-apply-result.helper';

/** Everything this dialog reads out of DynamicDialogConfig.data. */
export interface BathdaiApplyResultDialogData {
  daiName?: string | null;
  apply: BathDaiApplyResult;
  roomNameByUid?: ReadonlyMap<string, string>;
}

type ApplyTone = 'success' | 'conflict' | 'failed' | 'superseded';

/** One outcome column inside a tab's table (e.g. 削除 / 追加). */
interface ApplyColumn {
  key: ApplyResultBlockKey;
  label: string;
}

/** One room's row: the affected days per column of the tab (empty array when the room has none). */
interface ApplyRow {
  roomName: string;
  days: Record<string, string[]>;
}

/** One legend row: the status labels in their tones, then what that status means. */
interface ApplyLegendRow {
  labels: { text: string; tone: ApplyTone }[];
  description: string;
}

/** One status tab: its header count plus a room-per-row table of the days it covers. */
interface ApplyTab {
  key: ApplyTone;
  label: string;
  count: number;
  tone: ApplyTone;
  /** conflicted / failed - show the "check 入浴管理" hint. */
  needsCheck: boolean;
  columns: ApplyColumn[];
  rows: ApplyRow[];
}

/**
 * Shown after a dai save that propagated into existing schedules. The status counts
 * (success / conflict / failure, plus superseded when any) are tabs; the active tab lays its days out
 * as a table, one row per room (a move touches two rooms) with a column per outcome (削除 / 追加 / 更新（移動なし）). Built
 * from the apply result the write endpoint returns (see buildApplyResultView).
 */
@Component({
  selector: 'caremoni-bathdai-apply-result-dialog',
  templateUrl: './bathdai-apply-result-dialog.component.html',
  styleUrls: ['./bathdai-apply-result-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BathdaiApplyResultDialogComponent implements OnInit {
  TEXT = BATH_ROOM_SETTING;
  BUTTON = BUTTON;

  daiName = '';
  view: ApplyResultView = { summary: { success: 0, conflicted: 0, failed: 0, superseded: 0 }, blocks: [], planFailed: false, daiNotFound: false };
  planFailedMessage = '';
  tabs: ApplyTab[] = [];
  activeTabKey: ApplyTone = 'success';

  readonly legend: ApplyLegendRow[] = [
    {
      labels: [
        { text: BATH_ROOM_SETTING.APPLY_LEGEND_SUCCESS_LABEL, tone: 'success' },
        { text: BATH_ROOM_SETTING.APPLY_BLOCK_UPDATED, tone: 'success' },
      ],
      description: BATH_ROOM_SETTING.APPLY_LEGEND_SUCCESS,
    },
    { labels: [{ text: BATH_ROOM_SETTING.APPLY_BLOCK_CONFLICTED, tone: 'conflict' }], description: BATH_ROOM_SETTING.APPLY_LEGEND_CONFLICT },
    { labels: [{ text: BATH_ROOM_SETTING.APPLY_BLOCK_FAILED, tone: 'failed' }], description: BATH_ROOM_SETTING.APPLY_LEGEND_FAILED },
  ];

  private readonly BLOCK_LABEL: Record<ApplyResultBlockKey, string> = {
    updated: BATH_ROOM_SETTING.APPLY_BLOCK_UPDATED,
    added: BATH_ROOM_SETTING.APPLY_BLOCK_ADDED,
    removed: BATH_ROOM_SETTING.APPLY_BLOCK_REMOVED,
    conflicted: BATH_ROOM_SETTING.APPLY_BLOCK_CONFLICTED,
    failed: BATH_ROOM_SETTING.APPLY_BLOCK_FAILED,
    superseded: BATH_ROOM_SETTING.APPLY_BLOCK_SUPERSEDED,
  };

  constructor(public ref: DynamicDialogRef, public config: DynamicDialogConfig) {
    this.config.showHeader = false;
    // Wider/taller than the confirm dialog so the room-per-row table (room + 削除 + 追加 + 更新（移動なし）) sits comfortably.
    this.config.style = { width: this.config.width ?? '880px', maxWidth: '96vw', height: 'auto' };
    this.config.contentStyle = { 'border-radius': '12px' };
    this.config.styleClass = 'bathdai-apply-result-panel';
  }

  ngOnInit(): void {
    const data = (this.config?.data ?? {}) as BathdaiApplyResultDialogData;
    this.daiName = data.daiName ?? '';
    this.view = buildApplyResultView(data.apply ?? ({} as BathDaiApplyResult), data.roomNameByUid);
    this.planFailedMessage = this.TEXT.APPLY_PLAN_FAILED(this.daiName);
    this.tabs = this.buildTabs(this.view);
    this.activeTabKey = this.tabs[0]?.key ?? 'success';
  }

  get activeTab(): ApplyTab | undefined {
    return this.tabs.find(t => t.key === this.activeTabKey);
  }

  select(tone: ApplyTone): void {
    this.activeTabKey = tone;
  }

  roomLabel(roomName: string): string {
    return roomName || this.TEXT.APPLY_ROOM_UNKNOWN;
  }

  daysFor(row: ApplyRow, colKey: ApplyResultBlockKey): string[] {
    return row.days[colKey] ?? [];
  }

  close(): void {
    this.ref.close();
  }

  /**
   * Success / conflict / failure are always present so the tabs read as a full status line; superseded
   * is added only when it happened. Each tab pivots its outcome blocks into a table (see makeTab).
   */
  private buildTabs(view: ApplyResultView): ApplyTab[] {
    const byKey = new Map<ApplyResultBlockKey, ApplyResultBlock>(view.blocks.map(b => [b.key, b]));
    const s = view.summary;
    const tabs: ApplyTab[] = [
      this.makeTab('success', this.TEXT.APPLY_SUMMARY_SUCCESS(s.success), s.success, false, ['removed', 'added', 'updated'], byKey),
      this.makeTab('conflict', this.TEXT.APPLY_SUMMARY_CONFLICT(s.conflicted), s.conflicted, true, ['conflicted'], byKey),
      this.makeTab('failed', this.TEXT.APPLY_SUMMARY_FAILED(s.failed), s.failed, true, ['failed'], byKey),
    ];
    if (s.superseded > 0) {
      tabs.push(this.makeTab('superseded', this.TEXT.APPLY_SUMMARY_SUPERSEDED(s.superseded), s.superseded, false, ['superseded'], byKey));
    }
    return tabs;
  }

  /**
   * Pivots the given outcome blocks into one tab: a column per DECLARED outcome (kept even when it
   * has no data, so the table shape stays stable and an empty column renders as — cells rather than
   * collapsing away) and a row per room that has data, unioned across the blocks in the order the
   * server returned them, so a move's source (削除) and target (追加) rooms both appear each on
   * their own row.
   */
  private makeTab(
    key: ApplyTone,
    label: string,
    count: number,
    needsCheck: boolean,
    blockKeys: ApplyResultBlockKey[],
    byKey: Map<ApplyResultBlockKey, ApplyResultBlock>,
  ): ApplyTab {
    const presentKeys = blockKeys.filter(k => byKey.has(k));
    // Every declared column for the tab, not only the ones that happened to have data: the success
    // tab always shows 削除 / 追加 / 更新（移動なし） so the layout does not change with the data.
    const columns: ApplyColumn[] = blockKeys.map(k => ({ key: k, label: this.BLOCK_LABEL[k] }));

    const roomOrder: string[] = [];
    const rowByRoom = new Map<string, ApplyRow>();
    for (const k of presentKeys) {
      for (const group of byKey.get(k)!.rooms) {
        let row = rowByRoom.get(group.roomName);
        if (!row) {
          row = { roomName: group.roomName, days: {} };
          rowByRoom.set(group.roomName, row);
          roomOrder.push(group.roomName);
        }
        row.days[k] = group.days;
      }
    }

    return { key, label, count, tone: key, needsCheck, columns, rows: roomOrder.map(r => rowByRoom.get(r)!) };
  }
}
